import { useState } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { useAsyncEffect } from 'client/utils/use-async-effect';
import { LoadingIndicator } from '@components/loading-indicator';
import { BtnSecondary } from '@components/buttons';
import { fmtFullTime } from 'shared/dateutil';
import { useTimezone } from 'client/lib/hooks';
import { UserProfileIcon } from '@components/avatars/user-profile-icon';
import { IcoDownload } from '@components/icons';

type AttendanceData = RpxResponse<typeof rpx.meetingRsvps.getMeetingAttendance>;
type RsvpData = RpxResponse<typeof rpx.meetingRsvps.getMeetingRsvps>;

export function AttendanceTab({
  courseId,
  meetingId,
  meetingTitle,
  meetingStatus,
}: {
  courseId: UUID;
  meetingId: UUID;
  meetingTitle: string;
  meetingStatus: string;
}) {
  const [data, setData] = useState<AttendanceData | undefined>(undefined);
  const [rsvps, setRsvps] = useState<RsvpData | undefined>(undefined);
  const timezone = useTimezone();

  useAsyncEffect(async () => {
    const [attendanceResult, rsvpResult] = await Promise.all([
      rpx.meetingRsvps.getMeetingAttendance({ courseId, meetingId }),
      rpx.meetingRsvps.getMeetingRsvps({ courseId, meetingId }),
    ]);
    setData(attendanceResult);
    setRsvps(rsvpResult);
  }, [meetingId]);

  if (!data) {
    return <LoadingIndicator />;
  }

  const { attendees, absentees, totalMembers } = data;
  const pct = totalMembers > 0 ? Math.round((attendees.length / totalMembers) * 100) : 0;

  const hasRsvps = rsvps && rsvps.length > 0;
  const isEnded = meetingStatus === 'ended';

  return (
    <div class="py-8">
      {isEnded && <AttendanceRate attended={attendees.length} total={totalMembers} pct={pct} />}

      {!isEnded && (
        <div class="flex items-center gap-3 p-4 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600">
          <svg
            class="w-5 h-5 shrink-0 text-indigo-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
            />
          </svg>
          {hasRsvps
            ? 'Full attendance tracking and comparison with RSVPs will be available after the meeting ends.'
            : "No RSVPs yet. They'll show here as members respond. Attendance data appears once the meeting ends."}
        </div>
      )}

      {hasRsvps && (
        <div class="mt-8">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <RsvpIcon />
              <h3 class="text-base font-semibold text-gray-800">RSVPs ({rsvps.length})</h3>
            </div>
            <BtnSecondary
              class="flex items-center gap-1.5"
              onClick={() => downloadRsvpCsv(rsvps, timezone, meetingTitle, isEnded)}
            >
              <IcoDownload class="w-4 h-4" />
              Download CSV
            </BtnSecondary>
          </div>
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-gray-500">
                <th class="pb-2 font-medium" style="width: 40px"></th>
                <th class="pb-2 font-medium">Name</th>
                <th class="pb-2 font-medium">Email</th>
                <th class="pb-2 font-medium">RSVP Date</th>
                {isEnded && <th class="pb-2 font-medium">Attended</th>}
              </tr>
            </thead>
            <tbody>
              {rsvps.map((r) => (
                <tr key={r.userId} class="border-b border-gray-100">
                  <td class="py-2">
                    <UserProfileIcon
                      user={{ name: r.name, profilePhotoUrl: r.profilePhotoUrl }}
                      size="size-6"
                      maxWidth={48}
                    />
                  </td>
                  <td class="py-2 text-gray-800">{r.name}</td>
                  <td class="py-2 text-gray-600">{r.email}</td>
                  <td class="py-2 text-gray-500">
                    {fmtFullTime(r.createdAt, { timeZone: timezone })}
                  </td>
                  {isEnded && (
                    <td class="py-2">
                      {r.joinedAt ? (
                        <span class="text-green-600 font-medium">Yes</span>
                      ) : (
                        <span class="text-gray-400">—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isEnded && (
        <>
          <div class="flex items-center justify-between mb-4 mt-8">
            <div class="flex items-center gap-2">
              <AttendedIcon />
              <h3 class="text-base font-semibold text-gray-800">Attended ({attendees.length})</h3>
            </div>
            {attendees.length > 0 && (
              <BtnSecondary
                class="flex items-center gap-1.5"
                onClick={() => downloadCsv(attendees, timezone, meetingTitle)}
              >
                <IcoDownload class="w-4 h-4" />
                Download CSV
              </BtnSecondary>
            )}
          </div>

          {attendees.length === 0 ? (
            <div class="text-sm text-gray-500 py-8 text-center border border-gray-200 rounded-lg bg-gray-50">
              No attendance recorded yet. Share the bridge page link with attendees.
            </div>
          ) : (
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-gray-200 text-left text-gray-500">
                  <th class="pb-2 font-medium">Name</th>
                  <th class="pb-2 font-medium">Email</th>
                  <th class="pb-2 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {attendees.map((a) => (
                  <tr key={a.userId} class="border-b border-gray-100">
                    <td class="py-2 text-gray-800">{a.name}</td>
                    <td class="py-2 text-gray-600">{a.email}</td>
                    <td class="py-2 text-gray-500">
                      {fmtFullTime(a.joinedAt, { timeZone: timezone })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {isEnded && absentees.length > 0 && (
        <div class="mt-10">
          <div class="flex items-center gap-2 mb-4">
            <AbsentIcon />
            <h3 class="text-base font-semibold text-gray-800">
              Didn't attend ({absentees.length})
            </h3>
          </div>
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-gray-500">
                <th class="pb-2 font-medium">Name</th>
                <th class="pb-2 font-medium">Email</th>
              </tr>
            </thead>
            <tbody>
              {absentees.map((a) => (
                <tr key={a.userId} class="border-b border-gray-100">
                  <td class="py-2 text-gray-500">{a.name}</td>
                  <td class="py-2 text-gray-500">{a.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AttendanceRate({
  attended,
  total,
  pct,
}: {
  attended: number;
  total: number;
  pct: number;
}) {
  return (
    <div class="flex items-center gap-6 p-5 bg-gray-50 rounded-lg border border-gray-200">
      <div class="relative w-16 h-16 shrink-0">
        <svg viewBox="0 0 36 36" class="w-16 h-16 -rotate-90">
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" stroke-width="3" />
          <circle
            cx="18"
            cy="18"
            r="15.5"
            fill="none"
            stroke={pct > 0 ? '#22c55e' : '#e5e7eb'}
            stroke-width="3"
            stroke-dasharray={`${pct * 0.974} 100`}
            stroke-linecap="round"
          />
        </svg>
        <div class="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-800">
          {pct}%
        </div>
      </div>
      <div>
        <p class="text-2xl font-bold text-gray-800">
          {attended} <span class="text-base font-normal text-gray-400">of {total}</span>
        </p>
        <p class="text-sm text-gray-500">enrolled members attended via bridge page</p>
      </div>
    </div>
  );
}

function RsvpIcon() {
  return (
    <svg class="w-5 h-5 text-indigo-500" viewBox="0 0 20 20" fill="currentColor">
      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v1h8v-1zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-1a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 17v1h-3zM4.75 14.094A5.973 5.973 0 004 17v1H1v-1a3 3 0 013.75-2.906z" />
    </svg>
  );
}

function AttendedIcon() {
  return (
    <svg class="w-5 h-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
      <path
        fill-rule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clip-rule="evenodd"
      />
    </svg>
  );
}

function AbsentIcon() {
  return (
    <svg class="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
      <path
        fill-rule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clip-rule="evenodd"
      />
    </svg>
  );
}

function downloadCsv(
  attendees: AttendanceData['attendees'],
  timezone: string,
  meetingTitle: string,
) {
  const header = 'Name,Email,Joined At';
  const rows = attendees.map(
    (a) =>
      `"${(a.name || '').replace(/"/g, '""')}","${a.email}","${fmtFullTime(a.joinedAt, { timeZone: timezone })}"`,
  );
  const csv = [header, ...rows].join('\n');
  downloadBlob(csv, `attendance-${slugify(meetingTitle)}.csv`);
}

function downloadRsvpCsv(
  rsvps: RsvpData,
  timezone: string,
  meetingTitle: string,
  includeAttended: boolean,
) {
  const header = includeAttended ? 'Name,Email,RSVP Date,Attended' : 'Name,Email,RSVP Date';
  const rows = rsvps.map((r) => {
    const base = `"${(r.name || '').replace(/"/g, '""')}","${r.email}","${fmtFullTime(r.createdAt, { timeZone: timezone })}"`;
    return includeAttended ? `${base},"${r.joinedAt ? 'Yes' : 'No'}"` : base;
  });
  const csv = [header, ...rows].join('\n');
  downloadBlob(csv, `rsvps-${slugify(meetingTitle)}.csv`);
}

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}

function downloadBlob(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
